import type { IconifyJSON } from '@iconify/types';
import { icons as lucideIcons } from '@iconify-json/lucide';
import { getIconData, iconToSVG, iconToHTML } from '@iconify/utils';
import { DEFAULT_AMENITY_LUCIDE_ICON, resolveAmenityLucideIconName } from '~/utils/amenityIconMap';

const lucideData = lucideIcons as IconifyJSON;

function lucideSvgForName(name: string, size: number) {
  let data = getIconData(lucideData, name);
  if (!data) data = getIconData(lucideData, DEFAULT_AMENITY_LUCIDE_ICON);
  if (!data) return null;
  const s = String(size);
  const result = iconToSVG(data, { width: s, height: s });
  return iconToHTML(result.body, {
    ...result.attributes,
    width: s,
    height: s,
    'aria-hidden': 'true',
  });
}

export type AmenityLocaleRow = {
  en?: string | null;
  ru?: string | null;
  ar?: string | null;
};

export function pickAmenityLabel(row: AmenityLocaleRow | null | undefined, locale: string): string {
  if (!row || typeof row !== 'object') return '';
  const order = locale === 'ru' ? ['ru', 'en', 'ar'] : locale === 'ar' ? ['ar', 'en', 'ru'] : ['en', 'ru', 'ar'];
  for (const k of order) {
    const v = row[k as keyof AmenityLocaleRow];
    if (v != null && String(v).trim()) return String(v).trim();
  }
  return '';
}

export function amenityLabelsFromApi(amenities: unknown, locale: string = 'en'): string[] {
  if (!amenities || !Array.isArray(amenities)) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const item of amenities) {
    let label = '';
    if (typeof item === 'string') {
      label = item.trim();
    } else if (item && typeof item === 'object') {
      label = pickAmenityLabel(item as AmenityLocaleRow, locale);
    }
    if (!label) continue;
    const key = label.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(label);
  }
  return out;
}

export function amenityIconInnerSvg(label: string): { viewBox: string; inner: string } {
  const name = resolveAmenityLucideIconName(label);
  let data = getIconData(lucideData, name);
  if (!data) data = getIconData(lucideData, DEFAULT_AMENITY_LUCIDE_ICON);
  if (!data) return { viewBox: '0 0 24 24', inner: '' };
  const result = iconToSVG(data);
  return { viewBox: result.attributes.viewBox, inner: result.body };
}

export function amenityBadgeSvgHtml(label: string, size = 22): string {
  const name = resolveAmenityLucideIconName(label);
  return lucideSvgForName(name, size) ?? '';
}
